// De lijst in Taken als boom, zoals de lijstweergave van Planner (Rens, 23/10/2026): per klant de rijen in de volgorde
// van Planner (msdyn_displaysequence), een fase als rij met de taken eronder ingesprongen, tot drie lagen. Een fase
// zonder zichtbare taak verdwijnt mee met de filter; een ingeklapte fase houdt haar taken verborgen. Sorteren gebeurt
// binnen een fase, zoals in Planner.
import { tasksByFase } from "./fases";
import { deadline, URGENCY } from "./planning";
import type { Fase, Task } from "./types";

export type TreeSort = "planner" | "deadline" | "prio" | "changed" | "title";
export const TREE_SORTS: [TreeSort, string][] = [["planner", "Volgorde van Planner"], ["deadline", "Op deadline"], ["prio", "Op prioriteit"], ["changed", "Laatst gewijzigd"], ["title", "Op titel"]];

export type TreeRow =
  | { kind: "fase"; fase: Fase; level: number; /** de zichtbare taken eronder, ook in subfases */ tasks: Task[]; closed: boolean }
  | { kind: "task"; task: Task; level: number };

type Node = { kind: "fase"; fase: Fase; order: number } | { kind: "task"; task: Task; order: number };

/**
 * tasks: de zichtbare taken van één klant (al gefilterd); fases: alle fases van die klant; fasesMap: alle fases
 * (voor het pad); closed: ingeklapte fases.
 */
export function faseTree(tasks: Task[], fases: Fase[], fasesMap: Map<string, Fase>, closed: Set<string>, sort: TreeSort, today: string): TreeRow[] {
  const ids = new Set(fases.map((f) => f.id));
  const under = tasksByFase(tasks, fasesMap);
  const end = (n: Node) => (n.kind === "fase" ? n.fase.end : n.task.end);
  const changed = (n: Node) => (n.kind === "fase" ? (under.get(n.fase.id) || []).map((t) => t.modified).sort().pop() || "" : n.task.modified);
  const title = (n: Node) => (n.kind === "fase" ? n.fase.title : n.task.title);
  const prio = (n: Node) => (n.kind === "fase" ? 5 : n.task.priority);
  const cmp = (a: Node, b: Node): number => {
    switch (sort) {
      case "deadline": {
        const da = deadline(end(a), today), db = deadline(end(b), today);
        return URGENCY[da.key] - URGENCY[db.key] || (da.key === "verlopen" ? end(a).localeCompare(end(b)) : db.od - da.od) || a.order - b.order;
      }
      case "prio": return prio(a) - prio(b) || a.order - b.order;
      case "changed": return changed(b).localeCompare(changed(a)) || a.order - b.order;
      case "title": return title(a).localeCompare(title(b), "nl") || a.order - b.order;
      default: return a.order - b.order || title(a).localeCompare(title(b), "nl");
    }
  };
  // de fase van een taak die niet (meer) bij deze klant hoort, telt als geen fase
  const parentOf = (t: Task) => (t.faseId && ids.has(t.faseId) ? t.faseId : null);
  const children = (parent: string | null): Node[] => [
    ...fases.filter((f) => (parent ? f.parentId === parent : !f.parentId || !ids.has(f.parentId)) && under.has(f.id)).map((f): Node => ({ kind: "fase", fase: f, order: f.order })),
    ...tasks.filter((t) => parentOf(t) === parent).map((t): Node => ({ kind: "task", task: t, order: t.order ?? 0 })),
  ].sort(cmp);
  const out: TreeRow[] = [];
  const seen = new Set<string>(); // een lus in de data mag niet blijven hangen
  const walk = (parent: string | null, level: number) => {
    for (const n of children(parent)) {
      if (n.kind === "task") { out.push({ kind: "task", task: n.task, level }); continue; }
      if (seen.has(n.fase.id)) continue;
      seen.add(n.fase.id);
      const isClosed = closed.has(n.fase.id);
      out.push({ kind: "fase", fase: n.fase, level, tasks: under.get(n.fase.id) || [], closed: isClosed });
      if (!isClosed) walk(n.fase.id, level + 1);
    }
  };
  walk(null, 0);
  return out;
}
